import { useRef, useCallback } from "react";

/**
 * Returns a memoized function that will only call the passed function when it hasn't been called for the wait period
 * @param func The function to be called
 * @param wait Wait period after function hasn't been called for
 * @returns A memoized function that is debounced
 */
export const useDebouncedCallback = (func, wait) => {
  // Use a ref to store the timeout between renders
  // and prevent changes to it from causing re-renders
  const timeout = useRef();
  const lastArgs = useRef();

  const debounced = useCallback(
    async (...args) => {
      lastArgs.current = args;
      clearTimeout(timeout.current);

      const lastPromise = new Promise((resolve) => {
        timeout.current = setTimeout(async () => {
          clearTimeout(timeout.current);
          const result = func(...args);
          timeout.current = null;
          lastArgs.current = null;
          resolve(result);
        }, wait);
      });

      return lastPromise;
    },
    [func, wait]
  );

  // Flush: immediately execute pending call
  debounced.flush = async () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
      const result = await func(...(lastArgs.current || []));
      lastArgs.current = null;
      return result;
    }
  };

  return debounced;
};